/**
 * The thing that turns a white screen into a message.
 *
 * React unmounts the entire tree when a render throws and nothing catches it —
 * react-dom's own recovery path renders `null` at the root, which takes the
 * router and every route down with it. The result is a blank page, no message,
 * and a console stack containing nothing but minified react-dom internals.
 *
 * That is exactly what happened on 2026-07-28: one variable read before its
 * declaration in PropertyListsPage blanked the whole application, and the
 * visible stack pointed only at react-dom's error reporter. Diagnosing it took
 * a sourcemap, a bundle scan and a bisect — for a bug the compiler already knew
 * about. A boundary would have named the component in the first ten seconds.
 *
 * Deliberately dependency-free and defensive: this component runs when
 * something else has already failed, so it must not be able to fail itself.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Shown in the heading, so a route-level boundary can say where it broke. */
  scope?: string;
  /** Rendered instead of the default panel, if the caller wants its own. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null });
    // Keep the native console output too — it carries the real stack.
    console.error('[ErrorBoundary]', this.props.scope ?? 'app', error, info.componentStack);
  }

  private reset = () => this.setState({ error: null, componentStack: null });

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);

    // The component stack's first line is the component that threw. Surfacing
    // it is the whole point: "something went wrong" would have saved nobody.
    const culprit = (componentStack ?? '').trim().split('\n')[0]?.trim() ?? '';

    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <div className="w-full max-w-2xl rounded-lg border border-red-200 bg-red-50 p-6 dark:border-red-900/40 dark:bg-red-950/30">
          <h1 className="mb-1 text-lg font-semibold text-red-900 dark:text-red-200">
            {this.props.scope ? `${this.props.scope} failed to render` : 'Something failed to render'}
          </h1>
          <p className="mb-4 text-sm text-red-800 dark:text-red-300">
            The rest of Flow is fine — this is one screen, not the whole app.
          </p>

          <div className="mb-4 rounded-md bg-white/70 p-3 font-mono text-xs text-red-900 dark:bg-black/30 dark:text-red-200">
            {error.message || String(error)}
            {culprit && <div className="mt-1 opacity-70">{culprit}</div>}
          </div>

          <div className="flex gap-2">
            <button
              onClick={this.reset}
              className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
            >
              Try again
            </button>
            <button
              onClick={() => window.location.assign('/')}
              className="rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-800 hover:bg-red-100 dark:border-red-800 dark:text-red-200 dark:hover:bg-red-900/30"
            >
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}
